import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/http";
import { Input } from "@/components/ui/input";
import { Zap, Search } from "lucide-react";

// One-tap shorthand notes (Dental4Windows style). Filters by chart type / service category; resolves placeholders.
export const resolveSnippet = (text, ctx = {}) => {
  const today = new Date();
  const map = {
    client_first: (ctx.clientName || "").split(" ")[0] || "",
    client: ctx.clientName || "",
    service: ctx.serviceName || "",
    date: today.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" }),
    staff: ctx.staffName || "",
    last_settings: ctx.lastSettings || "",
  };
  return (text || "").replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
};

export const QuickNotes = ({ chartType, categoryId, clientId, excludeChartId, context = {}, onInsert, compact = false }) => {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [last, setLast] = useState(null);
  useEffect(() => { api.get("/note-snippets").then(({ data }) => setAll(data)).catch(() => setAll([])); }, []);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/clients/${clientId}/last-settings`, { params: { chart_type: chartType || "", exclude_chart_id: excludeChartId || "" } }).then(({ data }) => setLast(data)).catch(() => setLast(null));
  }, [clientId, chartType, excludeChartId]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return all.filter((n) => (!chartType || !n.chart_types?.length || n.chart_types.includes(chartType))
      && (!categoryId || !n.category_ids?.length || n.category_ids.includes(categoryId))
      && (!s || n.code.toLowerCase().includes(s) || n.label.toLowerCase().includes(s) || (n.text || "").toLowerCase().includes(s)));
  }, [all, q, chartType, categoryId]);
  if (!all.length) return null;
  const pick = (n) => onInsert({ ...n, resolved: resolveSnippet(n.text, { ...context, lastSettings: last?.summary || "" }), lastValues: last?.values || {} });
  return (
    <div className={`rounded-xl border border-dashed border-primary/40 bg-primary/[0.03] ${compact ? "p-2" : "p-2.5"}`} data-testid="quick-notes">
      <div className="flex items-center gap-2 mb-1.5">
        <Zap className="h-3.5 w-3.5 text-primary" /><span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Quick notes</span>
        {last?.found && <span className="text-[11px] text-muted-foreground truncate" title={last.summary} data-testid="quick-notes-last">{last.summary}</span>}
        {all.length > 6 && <div className="relative ml-auto"><Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find" className="h-7 w-28 pl-6 text-xs rounded-md" data-testid="quick-notes-search" /></div>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {list.map((n) => (
          <button type="button" key={n.id} onClick={() => pick(n)} title={n.text} className="group inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs hover:border-primary hover:bg-primary/5 transition-colors" data-testid={`quick-note-${n.code}`}>
            <span className="font-mono text-[10px] font-bold text-primary">{n.code}</span><span>{n.label}</span>
            {Object.keys(n.prefill || {}).length > 0 && <span className="text-[9px] text-muted-foreground group-hover:text-primary">+{Object.keys(n.prefill).length} settings</span>}
          </button>
        ))}
        {!list.length && <span className="text-xs text-muted-foreground">No quick notes for this treatment{q ? " match" : ""}.</span>}
      </div>
    </div>
  );
};
